import styled from 'styled-components';
import { useFormContext } from 'react-hook-form';

import { SectionColumn, SectionContainer } from 'components/DetailsView/Section';
import { DetailFormValue } from 'components/DetailsView/DetailFormValue';
import { DetailFormDatePicker } from 'components/DetailsView/DetailFormDatePicker';
import { CopyValue } from 'components/CopyValue';
import { dateFromString, formatDateAsAnniversary, getExtendedFormattedDatetime } from 'helpers/dateTimes';
import { DetailValue, DetailFormCheckbox, DetailLink } from 'components/DetailsView';

import { GetMicrostoreColumnQuery } from 'data/__generated__';
import { DetailFormStatusValue } from 'components/DetailsView/DetailFormStatusValue';
import { MICROSTORE_COLUMN_STATES_OPTIONS } from '../constants/states';
import { getColumnImage } from '../utils';
import { TotemImage } from 'components/TotemImage';

function getAvaLink(avaSalePlaceId?: number | null): string {
    return 'https://neocorner.app.ava-vending.fr/index.html#/saleplaces/' + avaSalePlaceId + '/info';
}

export const GeneralInfoSection = ({
    microstoreColumn,
}: {
    microstoreColumn: GetMicrostoreColumnQuery['microstoreColumn'];
}) => {
    const { register, watch, control } = useFormContext();
    const {
        _id,
        name,
        isPaused,
        shouldUpdateStockOnTransaction,
        shouldIgnoreReassortThresholds,
        startingDate,
        removalDate,
        state,
        type,
        createdAt,
        updatedAt,
        siteId,
        siteName,
        columnGroupId,
        columnGroupName,
        microstoreColumnTemplateId,
        microstoreColumnTemplateName,
        avaSalePlaceId,
        useDynamicDelivery,
        machineCode,
    } = microstoreColumn;

    const watchIsPaused = watch('isPaused', isPaused);
    const watchShouldUpdateStockOnTransaction = watch('shouldUpdateStockOnTransaction', shouldUpdateStockOnTransaction);
    const watchShouldIgnoreReassortThresholds = watch('shouldIgnoreReassortThresholds', shouldIgnoreReassortThresholds);
    const watchUseDynamicDelivery = watch('useDynamicDelivery', useDynamicDelivery);

    return (
        <SectionContainer title="Informations générales" isInitiallyOpen>
            <>
                <StyledColumnsSectionContainer>
                    <SectionColumn>
                        <Row>
                            <ColumnImage src={getColumnImage(type)} alt={type} />
                            <ModificationInfo>
                                <CopyValue value={_id} />
                                <ExternalResource>
                                    <ExternalResourceLink
                                        href={getAvaLink(avaSalePlaceId)}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        disabled={!avaSalePlaceId}
                                    >
                                        <TotemImage
                                            src="data:image/svg+xml,%3c?xml%20version=%271.0%27%20encoding=%27UTF-8%27?%3e%3csvg%20version=%271.1%27%20id=%27Layer_1%27%20xmlns=%27http://www.w3.org/2000/svg%27%20width=%271063px%27%20height=%27540px%27%20style=%27background-color:%20white;%27%3e%3cg%20transform=%27translate(43,%200)%27%3e%3cpath%20d=%27M4.28450199,387.567034%20L135.551222,118.855464%20C141.812432,100.890629%20158.900832,88%20179,88%20C198.501996,88%20215.169473,100.136014%20221.862223,117.267832%20L352.908923,385.377915%20C356.781572,392.024331%20359,399.753095%20359,408%20C359,432.852814%20338.852814,453%20314,453%20C296.594365,453%20281.496786,443.118042%20274.011634,428.658499%20L273.934,428.698391%20L179.227886,234.511339%20L88.6295131,424.31597%20C81.7891833,441.13904%2065.2797294,453%2046,453%20C20.5949015,453%200,432.405098%200,407%20C0,400.056886%201.53824845,393.47305%204.29253714,387.570699%20Z%27%20id=%27Combined-Shape%27%20fill=%27%23A6BBCC%27%3e%3c/path%3e%3cpath%20d=%27M633.284502,387.567034%20L764.551222,118.855464%20C770.812432,100.890629%20787.900832,88%20808,88%20C827.49645,88%20844.159993,100.129112%20850.856511,117.253217%20L982.589287,386.588189%20C986.04028,392.955444%20988,400.248863%20988,408%20C988,432.852814%20967.852814,453%20943,453%20C923.292835,453%20906.544465,440.331898%20900.453933,422.694739%20L808.227886,234.511339%20L718.025276,423.308595%20C711.443356,440.663606%20694.662337,453%20675,453%20C649.594902,453%20629,432.405098%20629,407%20C629,400.056841%20630.538269,393.472963%20633.292592,387.570582%20Z%27%20id=%27Combined-Shape-Copy%27%20fill=%27%23A0A2A1%27%3e%3c/path%3e%3cpath%20d=%27M237.279652,437.68651%20L427.402701,46.6998626%20C436.341,19.5773943%20461.884227,0%20492,0%20C518.633855,0%20541.691376,15.3121062%20552.849681,37.6134367%20L552.928079,37.6171005%20L749.469527,439.177405%20C755.5191,449.197112%20759,460.942052%20759,473.5%20C759,510.226936%20729.226936,540%20692.5,540%20C664.366733,540%20640.313818,522.529937%20630.597932,497.846488%20L493.102913,216.970504%20L359.555225,496.232857%20C349.792483,521.822569%20325.019025,540%20296,540%20C258.444637,540%20228,509.555363%20228,472%20C228,459.484617%20231.381078,447.758927%20237.27965,437.686513%20Z%27%20id=%27Combined-Shape-Copy-2%27%20fill=%27%23004677%27%20transform=%27translate(493.500000,%20270.000000)%20scale(1,%20-1)%20translate(-493.500000,%20-270.000000)%20%27%3e%3c/path%3e%3c/g%3e%3c/svg%3e"
                                            alt="Logo Ava"
                                            size={30}
                                        />
                                    </ExternalResourceLink>
                                    <DetailFormValue
                                        type="number"
                                        step="1"
                                        defaultValue={avaSalePlaceId}
                                        width="160px"
                                        {...register('avaSalePlaceId', { required: false, valueAsNumber: true })}
                                    />
                                </ExternalResource>
                                <DetailFormStatusValue
                                    state={state}
                                    options={MICROSTORE_COLUMN_STATES_OPTIONS}
                                    showHeading
                                />
                                <Row>
                                    <DetailValue
                                        label="Dernière mise à jour"
                                        value={getExtendedFormattedDatetime(new Date(updatedAt).getTime())}
                                    />
                                    <DetailValue
                                        label="Date de création"
                                        value={getExtendedFormattedDatetime(new Date(createdAt).getTime())}
                                    />
                                </Row>
                                <DetailFormCheckbox label="Pause" name="isPaused" defaultChecked={watchIsPaused} />
                                <DetailFormCheckbox
                                    label="Mettre à jour les stocks à chaque transactions"
                                    name="shouldUpdateStockOnTransaction"
                                    defaultChecked={watchShouldUpdateStockOnTransaction}
                                />
                                <DetailFormCheckbox
                                    label="Ignorer les seuils du réassort automatique"
                                    name="shouldIgnoreReassortThresholds"
                                    defaultChecked={watchShouldIgnoreReassortThresholds}
                                />
                                <DetailFormCheckbox
                                    label="Livraison dynamique (HotDrinks/Drinks/Snacks uniquement)"
                                    name="useDynamicDelivery"
                                    defaultChecked={watchUseDynamicDelivery}
                                />
                            </ModificationInfo>
                        </Row>
                    </SectionColumn>
                    <SectionColumn>
                        <DetailFormValue
                            label="Nom"
                            defaultValue={name}
                            {...register('name')}
                            width="100%"
                            data-test="microstore-column-name"
                        />
                        <DetailFormValue
                            label="Code machine (4 chiffres)"
                            defaultValue={machineCode ?? ''}
                            placeholder="0000"
                            maxLength={4}
                            width="100%"
                            data-test="microstore-column-machine-code"
                            {...register('machineCode', {
                                pattern: { value: /^\d{4}$/, message: 'Doit être 4 chiffres' },
                                setValueAs: (value) => (value === '' ? null : value),
                            })}
                        />
                        {watchUseDynamicDelivery && !machineCode && (
                            <small style={{ color: '#d97706' }}>
                                Code machine recommandé pour les colonnes en livraison dynamique
                            </small>
                        )}
                        <DetailLink label="Site" path="site" value={siteId} name={siteName} />
                        <DetailLink
                            label="Groupe de colonnes"
                            path="microstore-column-group"
                            value={columnGroupId}
                            name={columnGroupName}
                        />
                        <DetailLink
                            label="Template de colonne"
                            path="microstore-column-template"
                            value={microstoreColumnTemplateId}
                            name={microstoreColumnTemplateName}
                        />
                    </SectionColumn>
                    <SectionColumn>
                        <DetailFormDatePicker
                            label="Date de début"
                            name="startingDate"
                            defaultValue={startingDate}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                        />
                        <DetailFormDatePicker
                            label="Date de retrait"
                            name="removalDate"
                            defaultValue={removalDate}
                            control={control}
                            transformValueToDate={(value) => (value ? dateFromString(value) : null)}
                            transformDateToValue={(date) => (date ? formatDateAsAnniversary({ dateTime: date }) : null)}
                        />
                    </SectionColumn>
                </StyledColumnsSectionContainer>
            </>
        </SectionContainer>
    );
};

const StyledColumnsSectionContainer = styled.div`
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: 20px;
    width: 100%;
`;

const Row = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 15px;
`;

const ModificationInfo = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    flex: 1;
    min-width: 200px;
`;

const IMAGE_WIDTH = 100;
const IMAGE_HEIGHT = 300;

const ColumnImage = styled.img`
    width: ${IMAGE_WIDTH}px;
    height: ${IMAGE_HEIGHT}px;
    object-fit: contain;
`;

const ExternalResource = styled.div`
    display: flex;
    flex-direction: row;

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;

const ExternalResourceLink = styled.a<{ disabled: boolean }>`
    display: flex;
    flex-direction: row;
    align-items: center;

    color: ${({ theme }) => theme.ctaPrimaryColor};
    text-decoration: none;
    opacity: ${({ disabled }) => (disabled ? 0.5 : 1)};
    pointer-events: ${({ disabled }) => (disabled ? 'none' : 'default')};
    cursor: ${({ disabled }) => (disabled ? 'default' : 'pointer')};

    &:hover {
        text-decoration: underline;
    }

    & > :not(:first-child) {
        margin-left: 10px;
    }
`;
